import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import useDebounce from 'common/hooks/useDebounce';
import { useGetCustomerList } from 'common/queries-and-mutations/admin/customer';
import Badge from 'components/Badge';
import { Loader } from 'components/Loader';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import AdminTableProperties from 'components/TableWidget/AdminTableProperties';
import Paginator from 'components/TableWidget/Paginator';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { UserInfoDto } from 'service/dto/user.dto';
import { ListOrder, PaginatedListMeta } from 'types/general.type';
import { getProductBoughtName } from 'utils';
import male from '../../../../assets/male-big.png';
import female from '../../../../assets/female-big.png';
import { Gender } from 'apps/user/types/user.enum.type';
import { CUSTOMER_TABLE_REPORT } from 'constants/download-table-header';

export default function Customers() {
  const [urlParams, setUrlParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);
  const searchQueryString = urlParams.get('search_query') ?? '';
  const productBought = urlParams.get('productBought') ?? '';
  const dateFilter = urlParams.get('dateFilter') ?? '';
  const status = urlParams.get('status') ?? '';
  const currentPage = urlParams.get('page') ?? '1';
  const numberOfItemsPerPage = urlParams.get('items_per_page') || 10;

  const debounceSearch = useDebounce(searchQueryString, 1300);

  const getPaginationParams = useCallback(() => {
    return {
      page: +currentPage,
      take: +numberOfItemsPerPage,
      order: ListOrder.ASC
    };
  }, [currentPage, numberOfItemsPerPage]);

  const computedWhereOptions = useCallback(() => {
    if (searchQueryString.trim().length > 2) {
      return {
        search_query: searchQueryString
      };
    }
    return {
      search_query:
        searchQueryString.trim().length > 3 ? searchQueryString : '',
      productBought,
      date: dateFilter,
      status
    };
  }, [searchQueryString, productBought, dateFilter, status]);

  const { data: customerList, isFetching } = useGetCustomerList({
    whereOptions: computedWhereOptions(),
    paginationOptions: getPaginationParams(),
    enabled: isExecutingSearch
  });

  useEffect(() => {
    if (!isFetching && isExecutingSearch) {
      setIsExecutingSearch(false);
    }
  }, [isExecutingSearch, isFetching]);

  useEffect(() => {
    if (debounceSearch.length > 3) {
      setIsExecutingSearch(true);
    }
  }, [debounceSearch.length]);

  const columnHelper = createColumnHelper<UserInfoDto>();

  const getCustomerStatus = (
    user: UserInfoDto
  ): { label: string; type: 'default' | 'success' | 'warning' | 'danger' } => {
    if (!user.isActive) {
      return {
        label: 'In Active',
        type: 'danger'
      };
    }
    if (!user.isStepCompleted) {
      return {
        label: 'Pending',
        type: 'danger'
      };
    }
    if (user.sentForApproval && user.isAccountLocked) {
      return {
        label: 'Awaiting Approval',
        type: 'default'
      };
    }
    if (user.isAccountLocked && !user.isActive) {
      return {
        label: 'Completed',
        type: 'success'
      };
    }
    if (user.isStepCompleted && user.isAccountLocked && user.isActive) {
      return {
        label: 'Awaiting Signing',
        type: 'warning'
      };
    }

    return {
      label: 'In Progress',
      type: 'default'
    };
  };

  const columns = [
    columnHelper.accessor(row => row.gender, {
      id: 'gender',
      cell: info => (
        <span className="column-to-hide text-xs font-normal">
          <img
            className="h-14 w-14"
            src={info.getValue() === Gender.MALE ? male : female}
          />
        </span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]"></span>
      )
    }),
    columnHelper.accessor(row => row.othername, {
      id: 'othername',
      cell: info => (
        <span className="text-xs font-normal">{`${info.row.original.othername} ${info.row.original.surname}`}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Name</span>
      )
    }),
    columnHelper.accessor(row => row.email, {
      id: 'email',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Email</span>
      )
    }),
    columnHelper.accessor(row => row.phoneNumber, {
      id: 'phoneNumber',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          Phone Number
        </span>
      )
    }),
    columnHelper.accessor(row => row.planId, {
      id: 'planId',
      cell: info => (
        <span className="text-xs font-normal">
          {getProductBoughtName(info.getValue())}
        </span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          Product Bought
        </span>
      )
    }),
    columnHelper.accessor(row => row.createdAt, {
      id: 'createdAt',
      cell: info => (
        <span className="text-xs font-normal">
          {format(new Date(info.getValue()), 'dd MMM yyyy')}
        </span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Date</span>
      )
    }),
    columnHelper.accessor(row => row.isActive, {
      id: 'isActive',
      cell: info => (
        <Badge
          size="sm"
          title={getCustomerStatus(info.row.original).label}
          type={getCustomerStatus(info.row.original).type}
          className="w-[9rem]"
        />
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Status</span>
      )
    }),
    columnHelper.accessor(row => row.id, {
      id: 'id',
      cell: info => (
        <div className="column-to-hide">
          <Link
            to={`details/${info.getValue()}`}
            className="rounded-md bg-primary-900 p-2 px-5 text-white underline"
          >
            View
          </Link>
        </div>
      ),
      header: () => (
        <span className="column-to-hide text-xs font-semibold text-[#1F4173]">
          Action
        </span>
      )
    })
  ];

  const data = useMemo<UserInfoDto[]>(() => {
    const normalizedResult: any[] = (customerList?.data ?? []).map(item => ({
      id: item.id,
      email: item.email,
      isActive: item.isActive,
      surname: item.surname,
      planId: item.planId,
      gender: item.gender,
      othername: item.othername,
      phoneNumber: item.phoneNumber,
      accountType: item.accountType,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      deletedAt: item.deletedAt,
      isStepCompleted: item.isStepCompleted,
      sentForApproval: item.sentForApproval,
      isAccountLocked: item.isAccountLocked
    }));
    return normalizedResult;
  }, [customerList]);

  const listMetaData = (customerList?.meta ?? {}) as PaginatedListMeta;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting
    },
    initialState: {
      pagination: {
        pageSize: +numberOfItemsPerPage
      }
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    if (value.trim() === '') {
      urlParams.delete(name);
      setUrlParams(urlParams);
    }
    if (value.trim()) {
      urlParams.set(name, value);
      setUrlParams(urlParams);
    }
  };

  const goToNextPage = () => {
    setIsExecutingSearch(true);
    const nextPageValue = currentPage ? `${+currentPage + 1}` : '2';
    urlParams.set('page', nextPageValue);
    setUrlParams(urlParams);
  };

  const goToPreviousPage = () => {
    if (currentPage && +currentPage > 1) {
      setIsExecutingSearch(true);
      const previousPageValue = `${+currentPage - 1}`;
      urlParams.set('page', previousPageValue);
      setUrlParams(urlParams);
    }
  };

  const handleFilter = (filter: Record<string, any> | null) => {
    if (filter) {
      if (filter.dateFilter) {
        urlParams.set('dateFilter', filter.dateFilter);
      }
      if (filter.productBought) {
        urlParams.set('productBought', filter.productBought);
      }
      if (filter.status) {
        urlParams.set('status', filter.status);
      }
      setUrlParams(urlParams);
      setIsExecutingSearch(true);
    }
  };

  const clearFilter = () => {
    urlParams.delete('dateFilter');
    urlParams.delete('productBought');
    urlParams.delete('status');
    setUrlParams(urlParams);
    setIsExecutingSearch(true);
  };

  if (isFetching) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb title="Customers" />
      <AdminTableProperties
        searchQuery={searchQueryString}
        handleSearch={handleChange}
        exportData={data.map(cus => ({
          ...cus,
          productBought: getProductBoughtName(cus.planId),
          createdAt: format(new Date(cus.createdAt), 'dd MMM yyyy')
        }))}
        exportHeader={CUSTOMER_TABLE_REPORT}
        fileName={'customers'}
        handleFilter={handleFilter}
        clearFilter={clearFilter}
        filterData={{ status, dateFilter, productBought }}
      />
      <div className="mt-3 flex flex-col overflow-auto rounded-[5px] bg-white pb-20">
        <table
          className={clsx({
            'w-full min-w-full table-to-print': true,
            'flexs flex-col h-[30rem]': table.getRowModel().rows.length === 0
          })}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                className="w-full border-b text-xs text-[#1F4173]"
              >
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="p-5 text-left font-medium text-[#1F4173]"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? 'cursor-pointer select-none flex'
                            : '',
                          onClick: header.column.getToggleSortingHandler()
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          // asc: (
                          //   <div className="flex flex-row items-center pl-2">
                          //     <Icon name={Icons.Sort} />
                          //   </div>
                          // ),
                          // desc: (
                          //   <div className="flex flex-row items-center pl-2">
                          //     <Icon name={Icons.Sort} />
                          //   </div>
                          // )
                          asc: <></>,
                          desc: <></>
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr className="mt-10 pt-10 text-center text-xl font-bold">
                <td colSpan={7}>No customer found</td>
              </tr>
            )}

            {table.getRowModel().rows.map((row, cellIndex) => (
              <tr key={row.id} className="">
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className={clsx({
                      'p-5 text-xs': true,
                      'bg-[#F9F9F9]': cellIndex % 2 !== 0
                    })}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {listMetaData && listMetaData.pageCount > 1 && (
          <div className="mt-5 p-5">
            <Paginator
              numberOfPages={listMetaData.pageCount}
              page={+currentPage}
              hasNext={listMetaData.hasNextPage}
              hasPrevious={listMetaData.hasPreviousPage}
              numberOfItemsPerPage={+numberOfItemsPerPage}
              setNumberOfItemsPerPage={num => {
                // setPageSize(num);
                urlParams.set('items_per_page', num.toString());
                setUrlParams(urlParams);
              }}
              goToNextPage={goToNextPage}
              goToPreviousPage={goToPreviousPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
